import { FC, useMemo, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	EducationalContentItem,
	EducationalContentType,
} from "../../../domain/program.interface";
import { EducationalContentItemDialog } from "./EducationalContentItemDialog";

type Props = {
	items: EducationalContentItem[];
	newContentDays: number;
	onItemsChange: (items: EducationalContentItem[]) => void;
	onNewContentDaysChange: (value: number) => void;
};

const resolveDef = (
	value: EducationalContentItem["title"] | undefined,
): string => {
	if (!value) return "";
	const obj = value as Record<string, string>;
	return obj.def ?? Object.values(obj)[0] ?? "";
};

const typeLabel = (type: EducationalContentType): string => {
	switch (type) {
		case EducationalContentType.VIDEO:
			return "Video";
		case EducationalContentType.DOCUMENT:
			return "Document";
		case EducationalContentType.LINK:
			return "Link";
		default:
			return type;
	}
};

export const EducationalContentSection: FC<Props> = (props) => {
	const { items, newContentDays, onItemsChange, onNewContentDaysChange } =
		props;

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<
		EducationalContentItem | undefined
	>(undefined);

	const sortedItems = useMemo(() => {
		return [...items].sort((a, b) =>
			(b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
		);
	}, [items]);

	const handleOpenAdd = () => {
		setEditingItem(undefined);
		setDialogOpen(true);
	};

	const handleOpenEdit = (item: EducationalContentItem) => {
		setEditingItem(item);
		setDialogOpen(true);
	};

	const handleDelete = (id: string) => {
		onItemsChange(items.filter((it) => it.id !== id));
	};

	const handleSubmit = (item: EducationalContentItem) => {
		const exists = items.some((it) => it.id === item.id);
		const next = exists
			? items.map((it) => (it.id === item.id ? item : it))
			: [...items, item];
		onItemsChange(next);
		setDialogOpen(false);
		setEditingItem(undefined);
	};

	return (
		<Card variant="outlined">
			<CardContent>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="flex-start"
					mb={1}
				>
					<Box>
						<Typography variant="h6" gutterBottom>
							Educational Content
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Content shown in the Library tab of the participant mobile app.
						</Typography>
					</Box>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleOpenAdd}
					>
						Add content
					</Button>
				</Box>

				<Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
					<Grid item xs={12} sm={6} md={4}>
						<TextField
							fullWidth
							type="number"
							label="NEW badge timeframe (days)"
							value={newContentDays}
							onChange={(e) => {
								const n = parseInt(e.target.value, 10);
								onNewContentDaysChange(Number.isNaN(n) ? 0 : n);
							}}
							inputProps={{ min: 0 }}
							helperText="Items created within this many days show the NEW badge."
						/>
					</Grid>
				</Grid>

				{sortedItems.length === 0 ? (
					<Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
						<Typography variant="body2" color="text.secondary">
							No educational content yet. Click "Add content" to create one.
						</Typography>
					</Stack>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Type</TableCell>
								<TableCell>Title</TableCell>
								<TableCell>Category</TableCell>
								<TableCell>Created</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{sortedItems.map((item) => (
								<TableRow key={item.id} hover>
									<TableCell>
										<Chip size="small" label={typeLabel(item.type)} />
									</TableCell>
									<TableCell>{resolveDef(item.title)}</TableCell>
									<TableCell>{item.category}</TableCell>
									<TableCell>
										{item.createdAt
											? new Date(item.createdAt).toLocaleDateString()
											: ""}
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											aria-label="edit"
											onClick={() => handleOpenEdit(item)}
										>
											<EditIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											aria-label="delete"
											onClick={() => handleDelete(item.id)}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>

			<EducationalContentItemDialog
				open={dialogOpen}
				initialItem={editingItem}
				onCancel={() => {
					setDialogOpen(false);
					setEditingItem(undefined);
				}}
				onSubmit={handleSubmit}
			/>
		</Card>
	);
};
